import React, { useState } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useSelector } from "react-redux";
import {
  getPendingApprovals,
  approveExpense,
  rejectExpense,
  returnExpense,
  bulkApproveExpenses,
} from "../../../../services/workspace/reimbursement.service";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DefaultButton from "../../../components/buttons/defaultButton";
import CommonModal from "../../../components/modals/commonModel";
import DefaultBadge from "../../../components/badge/defaultBadge";
import DefaultTextarea from "../../../components/inputs/defaultTextarea";
import DefaultCheckbox from "../../../components/inputs/defaultCheckbox";
import { FiCheck, FiX, FiRotateCcw, FiEye } from "react-icons/fi";

const items = [
  { label: "Reimbursement", path: "/reimbursement" },
  { label: "Pending Approvals", path: "" },
];

const ManagerApprovals = () => {
  const { limit } = useSelector((state) => state.global);
  const navigate = useNavigate();

  const [modal, setModal] = useState({
    show: false,
    title: "",
    message: "",
    actionType: null,
    item: null,
    loading: false,
    notes: "",
  });

  const [selectedItems, setSelectedItems] = useState([]);

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    handleSearchChange,
    handlePageChange,
    fetchData,
  } = useCommonFetch(getPendingApprovals, {
    dataPath: "data.data",
    totalPath: "data.meta.total",
    errorMessage: "Failed to fetch pending approvals!",
    debounceDelay: 500,

    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [], action: {} };

      const transformedData = data.map((item) => ({
        ...item,
        id: { data: item.uuid, variant: "text" },
        checkbox: {
          variant: "custom",
          render: () => (
            <DefaultCheckbox
              checked={selectedItems.includes(item.uuid)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedItems([...selectedItems, item.uuid]);
                } else {
                  setSelectedItems(selectedItems.filter((id) => id !== item.uuid));
                }
              }}
            />
          ),
        },
        expense_number: {
          data: item.expense_number || "-",
          variant: "text",
          className: "font-semibold text-primary-600",
        },
        employee: {
          data: item.employee?.name || "-",
          variant: "text",
          className: "font-medium",
        },
        department: {
          data: item.employee?.department?.name || "-",
          variant: "text",
        },
        category: {
          data: item.category?.name || "-",
          variant: "text",
        },
        expense_date: {
          data: item.expense_date || "-",
          variant: item.expense_date ? "date" : "text",
        },
        amount: {
          data: item.amount || 0,
          variant: "currency",
          className: "font-semibold text-gray-900",
        },
        vendor: { data: item.vendor || "-", variant: "text" },
        submitted_at: {
          data: item.submitted_at || "-",
          variant: item.submitted_at ? "date-time" : "text",
        },
        policy_status: {
          variant: "custom",
          render: () => {
            if (item.exceeds_policy_limit) {
              return (
                <DefaultBadge
                  name="Exceeds Limit"
                  color="red"
                  icon="⚠️"
                />
              );
            }
            return (
              <DefaultBadge name="Within Limit" color="green" icon="✅" />
            );
          },
        },
      }));

      const actionButton = {
        view: (item) => navigate(`/reimbursement/expenses/${item.id.data}`, { state: { from: 'manager-approvals' } }),

        approve: (item) =>
          setModal({
            show: true,
            actionType: "approve",
            item,
            title: "Approve Expense",
            message: `Approve expense ${item.expense_number.data} for ${item.employee.data}?`,
            notes: "",
          }),

        reject: (item) =>
          setModal({
            show: true,
            actionType: "reject",
            item,
            title: "Reject Expense",
            message: `Reject expense ${item.expense_number.data}? Please provide reason.`,
            notes: "",
          }),

        return: (item) =>
          setModal({
            show: true,
            actionType: "return",
            item,
            title: "Return for Clarification",
            message: `Return expense ${item.expense_number.data} to employee? Specify what needs clarification.`,
            notes: "",
          }),
      };

      return { data: transformedData, action: actionButton };
    },
  });

  const handleActionConfirm = async () => {
    if (!modal.item) return;

    setModal((prev) => ({ ...prev, loading: true }));

    try {
      const payload = { notes: modal.notes };

      if (modal.actionType === "approve") {
        await approveExpense(modal.item.id.data, payload);
        toast.success("Expense approved successfully!");
      } else if (modal.actionType === "reject") {
        if (!modal.notes.trim()) {
          toast.error("Please provide rejection reason");
          setModal((prev) => ({ ...prev, loading: false }));
          return;
        }
        await rejectExpense(modal.item.id.data, payload);
        toast.success("Expense rejected!");
      } else if (modal.actionType === "return") {
        if (!modal.notes.trim()) {
          toast.error("Please specify what needs clarification");
          setModal((prev) => ({ ...prev, loading: false }));
          return;
        }
        await returnExpense(modal.item.id.data, payload);
        toast.success("Expense returned for clarification!");
      }

      fetchData();
      setModal({ show: false, item: null, actionType: null, notes: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || `Failed to ${modal.actionType} expense`
      );
      setModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) {
      toast.error("Please select expenses to approve");
      return;
    }

    try {
      await bulkApproveExpenses({ expense_ids: selectedItems });
      toast.success(`${selectedItems.length} expense(s) approved successfully!`);
      setSelectedItems([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to bulk approve expenses");
    }
  };

  const TableHeaderData = [
    { label: "Expense ID", key: "expense_number" },
    { label: "Employee", key: "employee" },
    { label: "Department", key: "department" },
    { label: "Category", key: "category" },
    { label: "Expense Date", key: "expense_date" },
    { label: "Amount", key: "amount" },
    { label: "Vendor", key: "vendor" },
    // { label: "Policy Status", key: "policy_status", sortable: false },
    { label: "Submitted At", key: "submitted_at" },
  ];

  return (
    <>
      <div className="flex flex-col gap-4">
        <PageTableHeader
          shouldShow
          breadcrumbs={items}
        />

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              {selectedItems.length} expense(s) selected
            </span>
            <div className="flex gap-2">
              <DefaultButton
                name="Clear Selection"
                variant="outline"
                size="sm"
                onClick={() => setSelectedItems([])}
              />
              <DefaultButton
                name={`Approve ${selectedItems.length} Expense(s)`}
                variant="success"
                size="sm"
                onClick={handleBulkApprove}
                icon={<FiCheck />}
              />
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm">
          <CommonTable
            TableHeaderData={TableHeaderData}
            tabledata={tableData.data || []}
            currentPage={currentPage}
            totalData={totalData}
            setCurrentPage={handlePageChange}
            loading={loading}
            action={tableData.action}
          />
        </div>
      </div>

      {/* Action Modal */}
      <CommonModal
        show={modal.show}
        headerTitle={modal.title}
        onClose={() =>
          setModal({ show: false, item: null, actionType: null, notes: "" })
        }
        modalSize="md"
      >
        <div className="space-y-4 py-4">
          <p className="text-gray-600 text-sm">{modal.message}</p>

          {/* Notes/Reason Input */}
          {(modal.actionType === "reject" || modal.actionType === "return") && (
            <DefaultTextarea
              label={
                modal.actionType === "reject"
                  ? "Rejection Reason *"
                  : "Clarification Required *"
              }
              value={modal.notes}
              onChange={(e) =>
                setModal((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder={
                modal.actionType === "reject"
                  ? "Explain why this expense is being rejected..."
                  : "Specify what information is needed..."
              }
              rows={4}
            />
          )}

          {modal.actionType === "approve" && (
            <DefaultTextarea
              label="Approval Notes (Optional)"
              value={modal.notes}
              onChange={(e) =>
                setModal((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Add any comments..."
              rows={3}
            />
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <DefaultButton
              name="Cancel"
              variant="outline"
              size="md"
              onClick={() =>
                setModal({ show: false, item: null, actionType: null, notes: "" })
              }
              disabled={modal.loading}
            />

            <DefaultButton
              name={
                modal.actionType === "approve"
                  ? "Approve"
                  : modal.actionType === "reject"
                  ? "Reject"
                  : "Return"
              }
              variant={
                modal.actionType === "approve"
                  ? "success"
                  : modal.actionType === "reject"
                  ? "danger"
                  : "warning"
              }
              size="md"
              loading={modal.loading}
              onClick={handleActionConfirm}
              icon={
                modal.actionType === "approve" ? (
                  <FiCheck />
                ) : modal.actionType === "reject" ? (
                  <FiX />
                ) : (
                  <FiRotateCcw />
                )
              }
            />
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default ManagerApprovals;

